import { useEffect } from "react";
import { Link } from "wouter";
import { useCreateEnrollToken, useEntitlement } from "../../api/queries";
import { isPaymentRequired } from "../../api/errors";
import { useAuth } from "../../auth/AuthContext";
import { canSeeBilling } from "../../auth/roleView";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { CopyButton } from "../../components/CopyButton";
import { Modal } from "../../components/Modal";
import { CapacityNotice } from "../billing/CapacityNotice";
import { nodeCapacityNotice } from "../billing/entitlementRules";
import "../../components/Form.css";
import "./add-host.css";

interface AddHostModalProps {
  open: boolean;
  fleetId: string;
  onClose: () => void;
}

/**
 * Mints a join token and shows the operator how to enroll a new host with
 * it. The token is held only in this mutation's transient state — never
 * written to localStorage, a query string, or a log (S-13) — and a fresh one
 * is minted every time the dialog opens; closing it drops whatever was
 * showing.
 */
export function AddHostModal({ open, fleetId, onClose }: AddHostModalProps) {
  const { session } = useAuth();
  const mintMutation = useCreateEnrollToken(fleetId);
  // Same gate as NodesListPage: this modal only ever opens for an admin
  // (node:create), and admin always holds billing:view too, so this never
  // fires a request the opener could only get a 403 from.
  const canViewBilling = canSeeBilling(session?.role);
  const { data: entitlement } = useEntitlement({ enabled: open && canViewBilling });
  const capacityNotice = entitlement ? nodeCapacityNotice(entitlement) : null;
  // Computed once as a plain boolean (rather than calling the type-guard
  // directly inside the JSX conditions below) so the "else" branch isn't
  // narrowed to `never` — `mintMutation.error` is already `ApiError` once
  // `isError` is true, and TS would otherwise treat "not payment-required"
  // on an already-`ApiError`-typed value as an empty type.
  const paymentRequired = mintMutation.isError && isPaymentRequired(mintMutation.error);

  useEffect(() => {
    if (open) {
      mintMutation.mutate();
    } else {
      mintMutation.reset();
    }
    // Re-mint only when the dialog opens (or the fleet changes under it) —
    // not on every render the mutation object itself changes identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fleetId]);

  const origin = window.location.origin;
  const token = mintMutation.data?.token;
  const expiresAt = mintMutation.data?.expiresAt;
  const oneLiner = token ? `curl -fsSL ${origin}/install.sh | COMEBYE_TOKEN=${token} sh` : "";
  const advanced = token ? `kelpie -server ${origin} -token ${token}` : "";

  return (
    <Modal open={open} title="Add a host" onClose={onClose}>
      <p className="add-host__intro">
        Run this on the new host to enroll it. Kelpie only ever connects outbound to the control plane — there's
        nothing to open on the host itself.
      </p>

      {/* Approaching/at capacity, shown whether or not minting has failed yet
          — an admin about to add a host should see this before they try, not
          only after a refusal. */}
      {!mintMutation.isError && capacityNotice && (
        <CapacityNotice severity={capacityNotice.severity} message={capacityNotice.message} />
      )}

      {mintMutation.isPending && <p className="add-host__status">Minting a join token…</p>}

      {paymentRequired && mintMutation.error && (
        // 402: refused because of a licensed limit, never because anything
        // running was touched (charter §1 safety corollary) — the server's
        // own message already says so; render it verbatim rather than a
        // generic failure so it's legible as a plan problem, not a bug.
        <Banner
          tone="warning"
          title="Node limit reached"
          className="add-host__payment-banner"
          action={
            <div className="add-host__payment-actions">
              {canViewBilling && (
                <Link href="/billing" className="banner__link">
                  View plan
                </Link>
              )}
              <Button variant="secondary" onClick={() => mintMutation.mutate()}>
                Try again
              </Button>
            </div>
          }
        >
          {mintMutation.error.message}
        </Banner>
      )}

      {mintMutation.isError && !paymentRequired && (
        <div className="add-host__status add-host__status--error" role="alert">
          <p>{mintMutation.error instanceof Error ? mintMutation.error.message : "Couldn't mint a join token."}</p>
          <Button variant="secondary" onClick={() => mintMutation.mutate()}>
            Try again
          </Button>
        </div>
      )}

      {token && expiresAt && (
        <>
          <div className="add-host__command">
            <label className="add-host__label" htmlFor="add-host-oneliner">
              One-liner
            </label>
            <code id="add-host-oneliner" className="add-host__code">
              {oneLiner}
            </code>
            <CopyButton getValue={() => oneLiner} label="Copy" />
          </div>

          <details className="add-host__advanced">
            <summary>Manual / advanced</summary>
            <div className="add-host__command">
              <label className="add-host__label" htmlFor="add-host-advanced">
                Run the agent directly
              </label>
              <code id="add-host-advanced" className="add-host__code">
                {advanced}
              </code>
              <CopyButton getValue={() => advanced} label="Copy" />
            </div>
          </details>

          <p className="add-host__note">
            This token is single-use and expires <strong>{new Date(expiresAt).toLocaleString()}</strong>. Adding
            more than one host? Mint another token for each — a used or expired one won't work twice.
          </p>

          <div className="form-actions">
            <Button variant="secondary" onClick={() => mintMutation.mutate()}>
              Mint another token
            </Button>
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
